import React, { useEffect, useRef, useState } from 'react'
import { useCoachChat } from './CoachChatContext'

/**
 * CoachVideoConversation component - handles video chat display and controls
 * Based on the candidate video conversation but adapted for coach use
 *
 * @param {Object} props
 * @param {Object} props.candidate - The candidate being chatted with
 */
const CoachVideoConversation = ({ candidate, _bgEffectsEnabled = false }) => {
  const { connectionState, chatManager } = useCoachChat()
  const selfVideoRef = useRef(null)
  const callerVideoRef = useRef(null)

  // Candidate media state (remote party paused/muted)
  const [candidateVideoPaused, setCandidateVideoPaused] = useState(false)
  const [_candidateAudioMuted, setCandidateAudioMuted] = useState(false)

  // Get video state from connection state
  const hasLocalVideo = connectionState.hasLocalVideo
  const hasRemoteVideo = connectionState.hasRemoteVideo
  const isCandidatePaused = candidateVideoPaused
  // CTS-9777: coach's own pause — freeze/overlay self-view for clarity
  const isLocalPaused = connectionState?.videoPaused || false

  // Register video refs with the chat manager
  useEffect(() => {
    if (chatManager) {
      // Set both refs at once
      chatManager.setVideoRefs(selfVideoRef.current, callerVideoRef.current)
    }
  }, [chatManager])

  // Update refs when they become available (after component mount)
  useEffect(() => {
    if (chatManager) {
      // Use a timeout to ensure refs are available after render
      const timeoutId = setTimeout(() => {
        if (selfVideoRef.current) {
          chatManager.setSelfVideoRef(selfVideoRef.current)
        }
        if (callerVideoRef.current) {
          chatManager.setCallerVideoRef(callerVideoRef.current)
        }
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [chatManager])

  // Set up local video stream when component mounts or hasLocalVideo changes
  useEffect(() => {
    if (hasLocalVideo && selfVideoRef.current && connectionState.localStream) {
      if (selfVideoRef.current.srcObject !== connectionState.localStream) {
        // console.log('CoachVideoConversation - Setting local video stream')
        selfVideoRef.current.srcObject = connectionState.localStream
        selfVideoRef.current.muted = true
      }
    }
  }, [hasLocalVideo, connectionState.localStream])

  // Listen for candidate media state changes
  useEffect(() => {
    const handleCandidateMediaStateChange = (data) => {
      /* console.log(
        'CoachVideoConversation - candidateMediaStateChanged event received:',
        data,
      ) */

      if (data.candidateEasyrtcid === candidate?.easyrtcid) {
        if (
          data.messageType === 'video_paused' ||
          data.messageType === 'video_unpaused'
        ) {
          setCandidateVideoPaused(data.messageType === 'video_paused')
          /* console.log(
            'CoachVideoConversation - Updated candidate video pause state:',
            data.messageType === 'video_paused',
          ) */
        } else if (
          data.messageType === 'audio_muted' ||
          data.messageType === 'audio_unmuted'
        ) {
          setCandidateAudioMuted(data.messageType === 'audio_muted')
          /* console.log(
            'CoachVideoConversation - Updated candidate audio mute state:',
            data.messageType === 'audio_muted',
          ) */
        }
      }
    }

    if (chatManager) {
      chatManager.addEventListener(
        'candidateMediaStateChanged',
        handleCandidateMediaStateChange,
      )
    }

    return () => {
      if (chatManager) {
        chatManager.removeEventListener(
          'candidateMediaStateChanged',
          handleCandidateMediaStateChange,
        )
      }
    }
  }, [chatManager, candidate?.easyrtcid])

  // Set up remote video stream when component mounts or hasRemoteVideo changes
  useEffect(() => {
    if (
      hasRemoteVideo &&
      callerVideoRef.current &&
      connectionState.remoteStream &&
      !isCandidatePaused
    ) {
      if (callerVideoRef.current.srcObject !== connectionState.remoteStream) {
        /* console.log('CoachVideoConversation - Setting remote video stream') */
        callerVideoRef.current.srcObject = connectionState.remoteStream
      }
    }
  }, [hasRemoteVideo, connectionState.remoteStream, isCandidatePaused])

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col">
      {/* Remote video (candidate) - fills grid space, stream centered via object-contain */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center rounded-lg bg-black">
        {hasRemoteVideo && !isCandidatePaused ? (
          <video
            ref={callerVideoRef}
            id="callerVideo"
            autoPlay
            playsInline
            className="absolute inset-0 h-full w-full object-contain"
          />
        ) : isCandidatePaused ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.125rem',
              fontWeight: '500',
            }}
          >
            Video Paused
          </div>
        ) : (
          <div style={{ color: '#6c757d', textAlign: 'center' }}>
            <div>{candidate?.displayName || 'Candidate'}</div>
            <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Waiting for video...
            </div>
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            bottom: '0.5rem',
            left: '0.5rem',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '500',
          }}
        >
          {candidate?.displayName || 'Candidate'}
        </div>

        {/* Local video (coach) - bottom right corner, docked inside container */}
        {hasLocalVideo && (
          <div className="aspect-video absolute bottom-4 right-4 z-10 flex w-[200px] items-center justify-center overflow-hidden rounded-lg bg-black shadow-lg">
            <video
              ref={selfVideoRef}
              id="selfVideo"
              autoPlay
              muted
              playsInline
              className={`h-full w-full -scale-x-100 object-contain ${
                isLocalPaused ? 'opacity-0' : ''
              }`}
            />
            {isLocalPaused && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 px-2 text-center font-['Roboto'] text-sm font-medium text-white">
                Paused
              </div>
            )}
            <div
              style={{
                position: 'absolute',
                bottom: '0.5rem',
                left: '0.5rem',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '500',
              }}
            >
              You
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CoachVideoConversation
